import {
  AuxiliaryDataStatus,
  type ForecastWeatherBar,
  ForecastWeatherBasis,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'

import { KOMA_COUNT } from '~/constants/koma'
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'

/**
 * 気象情報バーに出す対象日の気象の案内。
 * - `confirmedNotRecorded`: 確定済みで、確定時の予報が記録されていない (AC-DFC-015)
 * - `confirmedUnreadable`: 確定済みで、確定時の予報を読み出せない (AC-DFC-015a)
 * - `noWeatherData`: 気温・天気が予測・実績とも無い (AC-DFC-013)
 * - `none`: 案内を出さない
 */
export type TargetDayWeatherNotice =
  | 'none'
  | 'noWeatherData'
  | 'confirmedNotRecorded'
  | 'confirmedUnreadable'

/**
 * 気象情報バーとチャートが読む案内を 1 か所で決める。 予報の出所 (backend の
 * `forecastBasis`) が確定時の予報を出せない状態なら、 AC-DFC-013 より優先して
 * その案内にする (demand-forecast-common.md AC-DFC-013 注記)。 `forecastBasis`
 * が undefined (地点切替直後の placeholder) と UNSPECIFIED (field を持たない
 * 旧 backend) は LIVE と同じに扱い、 従来の判定に任せる。
 */
export function targetDayWeatherNotice(
  params: Parameters<typeof isTargetDayWeatherMissing>[0] & {
    forecastBasis: ForecastWeatherBasis | undefined
  },
): TargetDayWeatherNotice {
  switch (params.forecastBasis) {
    case ForecastWeatherBasis.CONFIRMED_NOT_RECORDED:
      return 'confirmedNotRecorded'
    case ForecastWeatherBasis.CONFIRMED_UNREADABLE:
      return 'confirmedUnreadable'
    default:
      return isTargetDayWeatherMissing(params) ? 'noWeatherData' : 'none'
  }
}

/**
 * 対象日の気温・天気が予測・実績とも 1 件も無いか (`demand-forecast-common.md`
 * AC-DFC-013)。 true の時、 気象情報バーの気温・天気の欄とチャートの気温の
 * 位置に「この日の気象データはありません」を出す。 気象情報バーと
 * チャートが同じ値を読むよう、 判定はここ 1 か所に置く。
 *
 * 次の場合は false (= 案内を出さない):
 * - `FETCH_FAILED` (本番では気象観測地点の解決に失敗した時)。 既存の取得失敗の
 *   表示を出す。 AC の「取得に失敗し、前回取得できたデータも無い場合」
 *   (定期取得ジョブの失敗 `jma-weather-data.md` AC-WD-005c や、 表示時の
 *   BigQuery の取得失敗) は、 ページには取得元名だけを持つ予報の無い応答として
 *   届き、 本関数は true を返す
 * - `weather` が undefined。 backend は予報が無い時も取得元名だけを持つ
 *   `weather` を返す (`page_weather_resolver.go` の livePageView)。 undefined に
 *   なるのは確定済みで確定時の予報を出せない場合 (AC-DFC-015 / 015a、
 *   `targetDayWeatherNotice` が別の案内にする) と、 一時切替の直後に前の地点の
 *   応答を描いている間で、 どちらも本 AC の対象外
 *
 * 露点は見ない (予測露点はもともと表示しない、 AC-DFC-009)。 気温は対象日の
 * 48 コマだけを見る。 前日・翌日の延長部と類似日の気温は対象日の気温系列ではない
 * ため、 あっても案内は出し、 チャートは従来どおり描く。
 */
export function isTargetDayWeatherMissing(params: {
  weather: ForecastWeatherBar | undefined
  actualWeather: ForecastWeatherBar | undefined
  weatherStatus: AuxiliaryDataStatus | undefined
  temperatureForecastSlots: KomaSlots | undefined
  temperatureActualSlots: KomaSlots | undefined
}): boolean {
  const { weather, actualWeather, weatherStatus } = params
  if (weatherStatus === AuxiliaryDataStatus.FETCH_FAILED) return false
  if (weather === undefined) return false

  const hasActualWeather = (actualWeather?.hourlyWeather.length ?? 0) > 0

  return !(
    hasForecastWeather(weather) ||
    hasActualWeather ||
    hasTargetDayValue(params.temperatureForecastSlots) ||
    hasTargetDayValue(params.temperatureActualSlots)
  )
}

/**
 * 気象情報バーの予報 (予報行の天気と最高/最低気温) の出し方。
 * - `notice`: 予測・実績とも無い。 案内「この日の気象データはありません」に置き換える (AC-DFC-013)
 * - `placeholder`: 過去日で予報のデータが無い。 予報行と最高/最低を「—」で埋め、 案内は出さない (AC-DFC-020)
 * - `values`: 値のある項目だけを出す (予報が一部でもある時と、 未来の断面で予報が無い時)
 */
export type ForecastRowMode = 'notice' | 'placeholder' | 'values'

export function forecastRowMode(params: {
  weather: ForecastWeatherBar | undefined
  /** `isTargetDayWeatherMissing` の結果 (チャートと同じ値)。 */
  isTargetDayWeatherMissing: boolean
  isPastTargetDate: boolean
}): ForecastRowMode {
  if (params.isTargetDayWeatherMissing) return 'notice'
  const { weather } = params
  if (
    params.isPastTargetDate &&
    weather !== undefined &&
    !hasForecastWeather(weather)
  ) {
    return 'placeholder'
  }
  return 'values'
}

/** 予報 (天気・最高/最低気温) が 1 項目でもあるか。 */
function hasForecastWeather(weather: ForecastWeatherBar): boolean {
  return (
    weather.hourlyWeather.length > 0 ||
    weather.maxTemperature !== undefined ||
    weather.minTemperature !== undefined
  )
}

function hasTargetDayValue(slots: KomaSlots | undefined): boolean {
  return (slots ?? []).slice(0, KOMA_COUNT).some((v) => v !== undefined)
}
